import * as React from 'react';

import { Button, EntityDetails, Row } from '@rematter/ui-web';
import { useAtom, useAtomValue } from 'jotai';
import { LucidePencil, LucidePlus } from 'lucide-react';

import * as state from '../../_state/lotDetail.state';
import { useTranslations } from '../../i18n/useTranslations';

import { AddMovementsToLotDialog } from './allocation/AddMovementsToLotDialog';
import { LotDetailOverviewContent } from './LotDetailOverviewContent';
import { LotDialog } from './LotDialog';
import { LotStatusBadge } from './LotStatusBadge';

import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

type LotDetailTab = 'overview';
export const LotDetailsView = () => {
  // Translations
  const t = useTranslations();
  // Location
  const [, setLocation] = useModuleLocation();

  // State
  const [currentTab, setCurrentTab] = React.useState<LotDetailTab>('overview');

  // Atoms

  const { data, loadingState, error, refetch } = useAtomValue(
    state.$remoteLotDetail,
  );
  const [addMovementsDialogOpen, setAddMovementsDialogOpen] = useAtom(
    state.$addMovementsDialogOpen,
  );
  const [editDialogOpen, setEditDialogOpen] = useAtom(state.$editDialogOpen);

  const lot = data ?? null;

  const handleBack = React.useCallback(() => {
    setLocation('/inventory/lots');
  }, [setLocation]);

  const handleOpenAddMovements = React.useCallback(() => {
    setAddMovementsDialogOpen(true);
  }, [setAddMovementsDialogOpen]);

  const handleCloseAddMovements = React.useCallback(() => {
    setAddMovementsDialogOpen(false);
  }, [setAddMovementsDialogOpen]);

  const handleAddMovementsSuccess = React.useCallback(() => {
    refetch();
  }, [refetch]);

  const handleOpenEdit = React.useCallback(() => {
    setEditDialogOpen(true);
  }, [setEditDialogOpen]);

  const handleCloseEdit = React.useCallback(() => {
    setEditDialogOpen(false);
  }, [setEditDialogOpen]);

  const handleEditSuccess = React.useCallback(() => {
    refetch();
    setEditDialogOpen(false);
  }, [refetch, setEditDialogOpen]);

  const isActive = lot?.status === 'Active';

  return (
    <>
      <EntityDetails
        className="@LotDetailView"
        error={error}
        loading={loadingState.isInitialLoading}
        onBackPressed={handleBack}
        primaryTitle={
          lot ? t.lotsListView.detail.title(lot.lotNumber) : 'Loading...'
        }
        badges={lot ? <LotStatusBadge status={lot.status} /> : null}
        subtitle={
          lot
            ? `${lot.zoneName ? `${lot.facilityName} - ${lot.zoneName} (${lot.zoneCode})` : lot.facilityName}${lot.description ? ` • ${lot.description}` : ''}`
            : undefined
        }
        headerActions={
          lot && isActive ? (
            <Row gap2>
              <Button
                icon={<LucidePencil />}
                variant="outline"
                color="neutral"
                onPress={handleOpenEdit}
              >
                {t.lotsListView.detail.edit}
              </Button>
              <Button
                icon={<LucidePlus />}
                color="primary"
                onPress={handleOpenAddMovements}
              >
                {t.lotsListView.detail.addMovements}
              </Button>
            </Row>
          ) : null
        }
        tabs={{
          overview: {
            title: t.lotsListView.detail.allocations,
            content: LotDetailOverviewContent,
          },
        }}
        currentTab={currentTab}
        onChangeTab={(tab) => setCurrentTab(tab as LotDetailTab)}
      />
      {lot && isActive && (
        <AddMovementsToLotDialog
          isOpen={addMovementsDialogOpen}
          onClose={handleCloseAddMovements}
          lotId={lot.id}
          facilityId={lot.facilityId}
          onSuccess={handleAddMovementsSuccess}
        />
      )}
      {lot && (
        <LotDialog
          isOpen={editDialogOpen}
          onClose={handleCloseEdit}
          onSuccess={handleEditSuccess}
          editingLot={{
            id: lot.id,
            lotNumber: lot.lotNumber,
            facilityId: lot.facilityId,
            facilityName: lot.facilityName,
            zoneId: lot.zoneId,
            zoneName: lot.zoneName,
            zoneCode: lot.zoneCode,
            status: lot.status,
            description: lot.description,
            quantitySummary: [],
            totalItemCount: 0,
            createdAt: lot.createdAt,
            updatedAt: lot.updatedAt,
          }}
        />
      )}
    </>
  );
};
